import React, { useEffect, useMemo, useReducer, useRef, useState } from 'react';

import moment, { Moment } from 'moment';
import { shallowEqual } from 'react-redux';

import { DateTimePicker } from '@material-ui/pickers';

import {
  replaceLanguageToMessageAction,
  resetLanguageStateAction,
  updateMessageErrorAction,
  updateMessageTypeAction,
  updateSubjectErrorAction,
} from './context/message-composer-actions';
import { messageReducer } from './context/message-composer-reducer';

import { OnMessageSendInput } from '../../typescript-types/messages/on-message-send-type';
import { UiButton } from '../../ui/ui-button';
import { UiDialog } from '../../ui/ui-dialog';
import { SplitButtonRenderProps, UiSplitButton } from '../../ui/ui-split-button';
import { confirm } from '../confirmation-dialog';
import Message from '../legacy-message';

import { MessageComposerProvider } from './context/message-composer-provider';
import { LanguageState } from './context/message-composer-state';
import { LegacyMessageComposerPreview } from './legacy-message-composer-preview';
import { MessageInput } from './message-input';

import {
  checkMessageForErrorsAndWarnings,
  checkSubjectForErrors,
} from './util/check-message-for-errors-and-warnings';
import { legacyGetLanguagePreferenceBannerMessages } from './util/legacy-language-preference-banner-messages';
import { convertMessageType } from './util/message-composer-template-parser';
import { CkEditorNewlineTrimmer } from '../../util/ckeditor-newline-trimmer';
import { combineWords, isDateOutsideBusinessHours } from '../../util/formatters';
import { LanguageCode, LANGUAGES } from '../../util/languages';
import { MESSAGE_TYPE, MessageType } from '../../util/message-types';

export interface MLMCProps {
  orgId?: number;
  //* ====== Message and subject values and configuration ====== */

  languageToMessage?: Record<LanguageCode, string> | null;
  languageToSubject?: Record<LanguageCode, string>;

  /**
   * Map of language codes to counts of employees who prefer the language
   */
  languageCounts?: Record<LanguageCode, number>;
  /**
   * The list of languages this composer should provide options for
   */
  languagesToSupport?: Array<LanguageCode>;
  /**
   * The message to prefill the editor with. Only works when languageToMessage is undefined
   */
  initialMessage?: string;
  /**
   * The language to default to. Only works when languageToMessage is undefined
   */
  defaultLanguage?: LanguageCode;

  //* ====== Message Type Configuration ====== */

  /**
   * Controls whether this composer supports the email message type. Supported on truthy
   */
  enableEmail: boolean;
  /**
   * Controls whether this composer supports the text message type. Supported on truthy
   */
  enableText: boolean;
  /**
   * The message type (email or text) to start the composer out on
   */
  initialType?: MessageType;
  /**
   * Control switching the message type of the composer externally. This is
   * used when we want the parent of the MLMC to handle switching between
   * MessageTypes.
   *
   * This can be used in conjunction with `showMessageTypeSwitcher` to fully
   * control the messageType switching behavior from outside this component
   */
  messageType?: MessageType;
  /**
   * If truthy, disables allowing the user to change the type. Use the messageType
   * prop to control switching the type externally
   *
   * This can be used in conjunction with `messageType` to fully
   * control the messageType switching behavior from outside this component
   */
  showMessageTypeSwitcher?: boolean;
  /**
   * Hide the subject field for emails
   */
  disableSubject?: boolean;

  //* ====== Message Preview Configuration ====== */

  /**
   * The list of employees to use to render the preview with
   */
  messagePreviewEmployees?: Employee[];
  /**
   * Which type of preview we are rendering - this is passed through to MessageComposerPreview to control
   * background color
   */
  messagePreviewType?: 'announcement' | 'survey';

  //* ====== Other Display Configuration ====== */

  /**
   * Disables sending buttons when truthy.
   * By default, the send buttons are disabled when the input is empty
   */
  disabled?: boolean;
  /**
   * On message change, the rich text editors will regain focus. This is necessary when updating text outside of
   * the component like with the personalize button
   */
  autofocus?: boolean;
  /**
   * Changes the "save button" to be the string passed in. The default behavior is `Send {messageType}`, optionally
   * adding the number of languages at the end if applicable
   * The button is not visible unless you provide `onSendMessage`
   */
  buttonText?: string;
  /**
   * A class that is included on the "save button". It can be used to change the positioning of the button
   *
   * TODO[PROD-3546]: This prop isn't used correctly by components calling it
   * with u-float-right
   */
  buttonClass?: string;
  /**
   * Whether or not this composer has the personalize button. Shown if truthy
   */
  canPersonalize?: boolean;
  /**
   * Whether to include employee or job applicant language
   */
  recipientType?: ContactType;
  /**
   * Whether or not this composer has the button that allows users to schedule future messages. Shown if truthy
   */
  canScheduleMessageInFuture?: boolean;
  /**
   * An override for the built-in preview showing logic. This will force the preview to always show, even if the message
   * doesn't require personalization (which is the default).
   */
  alwaysDisplayPreview?: boolean;
  /**
   * A function to customize the message to show in the message composer preview for each language
   * Used to display the prefix/affix we add on surveys
   */
  customizePreviewMessage?: (message: string, language: LanguageCode) => string;
  /**
   * Currently used for employee messaging, this option shows a secondary send button that allows the user to attached a contact card to the message thye send
   */
  allowSendWithVCard?: boolean;
  /**
   * Controls whether or not the add language button is visible. Exists for when
   * the consumer handles adding languages.
   */
  disableAddLanguage?: boolean;

  //* ====== URL/file Configuration ====== */

  canUploadFile?: boolean;

  //* ====== Translation plugin props ====== */

  /**
   * Enables the visibility of the translate toolbar button
   */
  canTranslate?: boolean;

  //* ====== Handler Functions ====== */

  onChange?: (message: Record<LanguageCode, string>) => void;
  onError?: (response: { errorMessage: string }) => void;
  onMessageSent?: () => void;
  onMessageTypeChange?: (type: MessageType) => void;
  /**
   * Called when loading state changes
   */
  onLoading?: (loading: boolean) => void;
  /**
   * The method that handles sending the actual message. It should return a promise that has one of three end states:
   * 1. Resolve successfully to any valid JSON object to indicate the message was sent successfully
   * 2. Resolve to an object that has errorMessage as a key to indicate that there was an issue sending the message,
   *      and that this component should display the message in errorMessage
   * 3. Resolve to `null` to indicate that the send message was cancelled by the delegated method here, either due to
   *      user intervention or some other condition that delegated method can detect
   */
  onSendMessage?: (
    onMessageSendInput: OnMessageSendInput,
  ) => Promise<
    { errorMessage: string } | { errorMessage?: undefined; [key: string]: any } | null | undefined
  >;
  onSubjectChange?: (subject: Record<LanguageCode, string>) => void;
  clearDraft?: () => void;
}

const SendButton = ({
  openClass,
  disabled: splitButtonDisabled,
  showVCardButton,
  buttonClass,
  isSending,
  displayedButtonText,
  sendMessage,
}: SplitButtonRenderProps & {
  showVCardButton?: boolean;
  buttonClass?: string;
  isSending: boolean;
  displayedButtonText: string;
  sendMessage: () => () => void;
}) => (
  <UiButton
    className={`u-boxShadow-none ${
      showVCardButton ? 'u-borderRadius-right-none u-border-white u-border-right' : ''
    } ${openClass ?? ''} ${buttonClass ?? ''}`}
    variant="primary"
    loading={isSending}
    disabled={splitButtonDisabled}
    onClick={sendMessage()}
    dataCy="send-message"
  >
    {displayedButtonText}
  </UiButton>
);

const ContactCardButton = ({
  disabled: splitButtonDisabled,
  isSending,
  sendMessage,
}: SplitButtonRenderProps & {
  isSending: boolean;
  sendMessage: (options?: { sendWithVCard: boolean }) => () => void;
}) => (
  <UiButton
    className="u-borderRadius-top-none"
    loading={isSending}
    disabled={splitButtonDisabled}
    onClick={sendMessage({ sendWithVCard: true })}
    variant="secondary"
    dataCy="send-with-contact-card"
  >
    Send With Contact Card
  </UiButton>
);

/**
 * Hello there, Team Engine developer! It's dangerous to go it alone, so take this:
 * This component is one of the most widely used, important, and fragile components
 * we have - this is due to a few things:
 *
 * 1. It tries to do too much (edit, view, save, clean, etc) and has bad isolation
 * 2. It covers a lot of use cases because of our support for multiple languages in many spots
 * 3. It's used in a variety of contexts that are a bit niche
 *
 * Use the cypress tests when modifying this component
 *
 * The most important thing to do is cover most of the places in the UI you are going to want to test that use
 * this component after you make a change here:
 * 1. The "Hiring Process" tab for a job => Edit any of the automated messages
 * 2. The "Edit Job Dialog" => Job Description tab
 * 3. The "Scheduled Message Toast" => Edit the message (must have this automated message turned on,
 *      and move an applicant through the right status)
 * 4. Sending a message to an applicant or an employee in a single thread
 * 5. Sending an announcement or survey to employees (and technically, editing survey reminders)
 * 6. Editing a scheduled announcement
 * 7. Editing an employee automation
 *
 * Importantly: any place that uses the "collectMessageToLanguage" method is spun up in a context
 * outside of normal React context - we'll need to update that component to use
 * Redux in children of this component https://teamengine.atlassian.net/browse/ENG-40
 */
export const LegacyMultiLanguageMessageComposer = ({
  orgId,
  enableEmail,
  enableText,
  allowSendWithVCard,
  alwaysDisplayPreview,
  autofocus,
  buttonClass,
  buttonText,
  canPersonalize,
  recipientType,
  canScheduleMessageInFuture,
  canTranslate,
  canUploadFile,
  clearDraft,
  customizePreviewMessage,
  defaultLanguage = 'en',
  disabled,
  initialMessage,
  initialType,
  messageType: controlledMessageType,
  languageToMessage: languageToMessageProp,
  languageToSubject: languageToSubjectProp,
  languagesToSupport,
  messagePreviewEmployees,
  messagePreviewType,
  onChange,
  onError,
  onMessageSent,
  onMessageTypeChange,
  onSendMessage,
  onSubjectChange,
  onLoading,
  showMessageTypeSwitcher,
  disableSubject,
  languageCounts,
  disableAddLanguage,
}: MLMCProps) => {
  // if messaging a monolingual group speaking a non-supported language, it's
  // possible for languagesToSupport to be an empty array, in that case, we
  // want to display the default editor
  const initialDisplayedLanguages = languagesToSupport?.length
    ? languagesToSupport
    : [defaultLanguage];
  const emptyLanguageToState = initialDisplayedLanguages.reduce<
    Record<LanguageCode, LanguageState>
  >(
    (acc, language) => ({
      ...acc,
      [language]: { message: '' },
    }),
    {},
  );
  const defaultLanguageToState = {
    ...emptyLanguageToState,
    [defaultLanguage]: { message: initialMessage || '' },
  };
  const defaultLanguageToSubject = initialDisplayedLanguages.reduce<Record<LanguageCode, string>>(
    (acc, languageCode) => ({
      ...acc,
      [languageCode]: '',
    }),
    {},
  );
  const [languageToSubject, setLanguageToSubject] = useState<Record<LanguageCode, string>>(
    languageToSubjectProp ?? defaultLanguageToSubject,
  );

  const [isSending, setIsSending] = useState<boolean>(false);
  const [isSchedulingMessage, setIsSchedulingMessage] = useState<boolean>(false);
  const [sendMessageAt, setSendMessageAt] = useState<Moment | null>(null);

  const initialMessageType =
    initialType || (enableEmail && !enableText ? MESSAGE_TYPE.EMAIL : MESSAGE_TYPE.TEXT);

  const initialLanguageToState = defaultLanguageToState;
  if (languageToMessageProp) {
    Object.keys(languageToMessageProp).forEach((key) => {
      initialLanguageToState[key] = { message: languageToMessageProp[key] };
    });
  }

  const [messageComposerState, messageComposerDispatch] = useReducer(messageReducer, {
    messageType: initialMessageType,
    languageToState: initialLanguageToState,
    languageCode: defaultLanguage,
    // TODO[PROD-3453]: this loading state is incomplete and is currently just translating
    isLoading: false,
    hasErrors: false,
    fileCount: 0,
    forceUpdate: false,
    disableSubject,
  });

  const {
    languageToState,
    messageType,
    isLoading,
    hasErrors: hasMessageInputErrors,
  } = messageComposerState;

  // This object is the original structure this component relied on before
  // context and it still uses it for some behavior especially interactions
  // with the parent. This needs to be memoized because it's slightly
  // expensive and can cause infinite loops if we calculate it too often
  const languageToMessage = useMemo(
    () =>
      Object.keys(languageToState).reduce((acc, key) => {
        acc[key] = languageToState[key].message;
        return acc;
      }, {} as Record<LanguageCode, string>),
    [languageToState],
  );

  const displayedLanguages = Object.keys(languageToState)
    .sort((a, b) => +(b === defaultLanguage) - +(a === defaultLanguage))
    .filter((lc) => (languagesToSupport ? languagesToSupport.includes(lc) : true));

  const emptyLanguageGroup = displayedLanguages.length === 0;

  const languagePreferenceBannerMessages = languageCounts
    ? legacyGetLanguagePreferenceBannerMessages(languageCounts, languageToMessage, 'en')
    : undefined;

  // Send loading state to the parent
  const prevIsLoading = useRef(isLoading);
  useEffect(() => {
    if (onLoading && prevIsLoading.current !== isLoading) {
      onLoading(isLoading);
      prevIsLoading.current = isLoading;
    }
  }, [isLoading, onLoading]);

  // This useEffect facilitates sending and receiving languageToMessage updates
  // between the parent component and this component
  // This can be removed when we move the context up higher and the parent
  // can tie into the state we're sending up.
  const prevLangToMessage = useRef(languageToMessage);
  const prevLangToMessageProp = useRef(languageToMessageProp);
  useEffect(() => {
    // Receiving from the parent.
    // Allow the parent to override the current languageToMessage by changing
    // what value they pass in. This is currently used for template messages
    if (
      languageToMessageProp &&
      !shallowEqual(prevLangToMessageProp.current, languageToMessageProp)
    ) {
      let propIsNotEqualToCurrentState = false;
      Object.keys(languageToMessageProp).forEach((lc) => {
        if (languageToMessage[lc] !== languageToMessageProp[lc]) {
          propIsNotEqualToCurrentState = true;
        }
      });

      if (propIsNotEqualToCurrentState) {
        messageComposerDispatch(
          // force consumers maintaining their own state to reflect this update
          replaceLanguageToMessageAction(languageToMessageProp, { forceUpdate: true }),
        );
      }

      prevLangToMessageProp.current = languageToMessageProp;
      // Sending to the parent
      // This is used to apply any changes to messages that occur in the reducer
    } else if (onChange && !shallowEqual(prevLangToMessage.current, languageToMessage)) {
      prevLangToMessage.current = languageToMessage;
      onChange(languageToMessage);
    }
  }, [languageToMessage, languageToMessageProp, onChange]);

  // this is used to send messageType to the parent
  // allows controlling messageType through the `messageType` prop
  useEffect(() => {
    let isUnmounted = false;
    if (!isUnmounted) {
      let nextType = controlledMessageType || initialMessageType;

      // If we're about to use a disabled type, switch it
      if (nextType === MESSAGE_TYPE.TEXT && !enableText) {
        nextType = MESSAGE_TYPE.EMAIL;
      }
      if (nextType === MESSAGE_TYPE.EMAIL && !enableEmail) {
        nextType = MESSAGE_TYPE.TEXT;
      }

      if (messageType !== nextType) {
        messageComposerDispatch(updateMessageTypeAction(nextType));
      }
      if (onMessageTypeChange) {
        onMessageTypeChange(nextType);
      }
    }
    return () => {
      isUnmounted = true;
    };
    // we need onMessageTypeChange to not be in this dependency array because the
    // function reference constantly changes which causes infinite loops
    // we also want to keep initialType out because if this hook
    // reacts to changes with both the controlled type and initial type it will
    // infinitely render as well. initialType is included in initialMessageType
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enableEmail, enableText, controlledMessageType]);

  const changeSubject = (languageCode: LanguageCode, subject: string) => {
    const newLanguageToSubject = {
      ...languageToSubject,
      [languageCode]: subject,
    };
    // We check the subject for errors in the MessageInput before we
    // call this function
    setLanguageToSubject(newLanguageToSubject);

    if (onSubjectChange) {
      onSubjectChange(newLanguageToSubject);
    }
  };

  /**
   * Dispatches the appropriate actions to affect context when message/subject
   * state is changed, ex. when updating a message type, adding/removing a
   * language
   * @param newLanguageToMessage new language => message state
   * @param newLanguageToSubject new language => subject state
   */
  const handleChange = (
    newLanguageToMessage: Record<LanguageCode, string>,
    newLanguageToSubject?: Record<LanguageCode, string>,
  ) => {
    messageComposerDispatch(replaceLanguageToMessageAction(newLanguageToMessage));
    if (newLanguageToSubject) {
      setLanguageToSubject(newLanguageToSubject);
    }

    if (onChange) {
      onChange(newLanguageToMessage);
    }

    if (onSubjectChange && newLanguageToSubject) {
      onSubjectChange(newLanguageToSubject);
    }
  };

  const handleMessageChange = (language: string, message: string) => {
    const newLanguageToMessage = {
      ...languageToMessage,
      [language]: message,
    };

    handleChange(newLanguageToMessage);
  };

  const sendMessage = (options?: { sendWithVCard: boolean }) => async () => {
    if (!onSendMessage) {
      return;
    }

    const simpleLanguageToMessage = CkEditorNewlineTrimmer.getSimpleTrimmedHash(
      languageToMessage,
      messageType,
    );

    let didError = false;
    Object.keys(simpleLanguageToMessage).forEach((languageCode) => {
      const subjectErr = checkSubjectForErrors(messageType, languageToSubject[languageCode]);
      const { error: messageErr } = checkMessageForErrorsAndWarnings(
        messageType,
        simpleLanguageToMessage[languageCode],
      );
      if (subjectErr || messageErr) {
        didError = true;
        if (subjectErr) {
          messageComposerDispatch(
            updateSubjectErrorAction({
              subjectError: subjectErr,
              languageCode,
            }),
          );
        }
        if (messageErr) {
          messageComposerDispatch(
            updateMessageErrorAction({ languageCode, messageError: messageErr }),
          );
        }
      }
    });

    if (didError) {
      return;
    }

    setIsSending(true);
    setIsSchedulingMessage(false);
    setSendMessageAt(null);

    const urlFileMappings: Array<UrlFileMapping> = [];
    const filesAttachedByLanguage: Record<LanguageCode, boolean> = {};
    Object.entries(languageToState).forEach(([languageCode, state]) => {
      if (!languageToMessage[languageCode]) {
        return;
      }
      filesAttachedByLanguage[languageCode] = Boolean(state.urlFileMappings);
      urlFileMappings.push(...(state.urlFileMappings?.filter(({ isDeleted }) => !isDeleted) || []));
    });

    let shouldSendMessage = new Set(Object.values(filesAttachedByLanguage)).size === 1;
    if (!shouldSendMessage) {
      const languagesWithAttachedFiles: Array<LanguageCode> = [];
      Object.entries(filesAttachedByLanguage).forEach(([languageCode, isFileAttached]) => {
        if (isFileAttached) {
          languagesWithAttachedFiles.push(LANGUAGES[languageCode]);
        }
      });

      const languagesWithAttachedFilesList =
        languagesWithAttachedFiles.length === 1
          ? languagesWithAttachedFiles[0]
          : combineWords(languagesWithAttachedFiles);

      shouldSendMessage = await confirm(
        `You have only attached a file to the ${languagesWithAttachedFilesList} version${
          languagesWithAttachedFiles.length > 1 ? 's' : ''
        } of this message. Send anyways?`,
        { confirmButtonLabel: displayedButtonText },
      );
    }

    if (!shouldSendMessage) {
      setIsSending(false);
      return;
    }

    const result = await onSendMessage({
      languageToMessage: simpleLanguageToMessage,
      messageType,
      languageToSubject: messageType === MESSAGE_TYPE.EMAIL ? languageToSubject : {},
      sendMessageAt,
      urlFileMappings,
      sendWithVCard: options?.sendWithVCard,
    });
    setIsSending(false);

    // We use null to specifically mean "this action was cancelled"
    if (result === null) {
      return;
    }

    if (result?.errorMessage) {
      // We'll only get here if the parent has a custom error
      // to throw that is outside of our checks in checkMessageForErrorsAndWarnings
      // in that case we'll just put the error on the english editor
      messageComposerDispatch(
        updateMessageErrorAction({ messageError: result.errorMessage, languageCode: 'en' }),
      );
      if (onError) {
        // TODO[PROD-3474]: this result is not used by consumers and not typed
        onError(result);
      }
    } else {
      // reset subjects and messages
      setLanguageToSubject(defaultLanguageToSubject);
      messageComposerDispatch(
        resetLanguageStateAction(
          emptyLanguageToState,
          // force consumers maintaining their own state to reflect this update
          { forceUpdate: true },
        ),
      );

      if (onMessageSent) {
        onMessageSent();
      }
    }

    if (clearDraft) {
      clearDraft();
    }
  };

  // TODO this logic can fully be handled by the reducer once
  // parent's are plugged in
  const handleSwitchType = () => {
    const nextType = messageType === MESSAGE_TYPE.TEXT ? MESSAGE_TYPE.EMAIL : MESSAGE_TYPE.TEXT;
    Object.keys(languageToMessage).forEach((key) => {
      const message = languageToMessage[key];
      const formattedMessage = convertMessageType(message, nextType);

      // Same as doing this in the reducer and firing off an update event
      handleMessageChange(key, formattedMessage);
    });

    if (onMessageTypeChange) {
      onMessageTypeChange(nextType);
    }

    messageComposerDispatch(updateMessageTypeAction(nextType));
  };

  const handleAddLanguage = (language: LanguageCode) => {
    const newLanguageToMessage = { ...languageToMessage, [language]: '' };
    const newLanguageToSubject = isTypeEmail
      ? { [language]: '', ...languageToSubject }
      : languageToSubject;

    handleChange(newLanguageToMessage, newLanguageToSubject);
  };

  const handleRemoveLanguage = (language: LanguageCode) => {
    const { [language]: ignoredMessage, ...newLanguageToMessage } = languageToMessage;
    const { [language]: ignoredSubject, ...newLanguageToSubject } = languageToSubject;

    handleChange(newLanguageToMessage, newLanguageToSubject);
  };

  const handleAddLanguageClick = (languageCode: LanguageCode) => () => {
    handleAddLanguage(languageCode);
  };

  const handleRemoveLanguageClick = (languageCode: LanguageCode) => () => {
    handleRemoveLanguage(languageCode);
  };

  const handleToggleDialog = () => {
    setIsSchedulingMessage(!isSchedulingMessage);
  };

  const handleScheduledTimeChange = (newSendMessageAt: Moment | null) => {
    setSendMessageAt(newSendMessageAt);
  };

  if (!enableEmail && !enableText) {
    return null;
  }

  if (messageType === null) {
    return null;
  }

  const displayedButtonText = buttonText || `Send ${messageType}`;
  const displayedScheduleButtonText = buttonText || `Schedule ${messageType}`;

  const isTypeEmail = messageType === MESSAGE_TYPE.EMAIL;

  let isScheduledMessageDisabled = false;
  if (canScheduleMessageInFuture) {
    displayedLanguages.forEach((key) => {
      const subjectErr = checkSubjectForErrors(messageType, languageToSubject[key]);
      const messageErr = languageToState[key].messageError;
      if ((isTypeEmail && subjectErr) || messageErr) {
        isScheduledMessageDisabled = true;
      }
    });
  }

  const shouldShowMessageSwitcher =
    typeof showMessageTypeSwitcher === 'undefined' || showMessageTypeSwitcher;

  let schedulingWarningMessage = null;
  if (isSchedulingMessage && sendMessageAt) {
    schedulingWarningMessage = isDateOutsideBusinessHours(sendMessageAt)
      ? 'Your time seems to be outside normal business hours. Please make sure this is what you want.'
      : null;
  }

  const showVCardButton = onSendMessage && allowSendWithVCard && messageType !== MESSAGE_TYPE.EMAIL;

  const isMessageEmpty =
    !languageToMessage[defaultLanguage] || (isTypeEmail && !languageToSubject[defaultLanguage]);
  const hasError = hasMessageInputErrors || isScheduledMessageDisabled;
  const isSendingDisabled =
    disabled || hasError || isMessageEmpty || isLoading || emptyLanguageGroup;

  const renderSendButton = (splitButtonProps: SplitButtonRenderProps) => (
    <SendButton
      disabled={isSendingDisabled}
      showVCardButton={showVCardButton}
      isSending={isSending}
      sendMessage={sendMessage}
      displayedButtonText={displayedButtonText}
      buttonClass={buttonClass}
      {...splitButtonProps}
    />
  );

  const renderContactCardButton = (splitButtonProps: SplitButtonRenderProps) => (
    <ContactCardButton isSending={isSending} sendMessage={sendMessage} {...splitButtonProps} />
  );

  return (
    <>
      {emptyLanguageGroup && (
        <Message variant="warning" canBeDismissed={false}>
          This selection contains no recipients. Add recipients to the group or make another
          selection.
        </Message>
      )}
      {displayedLanguages.map((language, i) => {
        // only focus the english (top) editor when multiple editors are visible
        const isLanguageFocusable = displayedLanguages.length > 1 ? language === 'en' : true;
        return (
          <div key={language} className={i > 0 ? 'u-margin-top' : ''}>
            <MessageComposerProvider
              messageComposerState={{ ...messageComposerState, languageCode: language }}
              dispatch={messageComposerDispatch}
            >
              {language === defaultLanguage ? null : (
                <div className="u-spread u-margin-top-sm u-margin-bottom-sm">
                  <div className="typo-h6">{LANGUAGES[language]}</div>
                  <UiButton
                    onClick={handleRemoveLanguageClick(language)}
                    variant="tertiary"
                    dataCy={`remove-language-textbox-${language}`}
                  >
                    Remove
                  </UiButton>
                </div>
              )}
              <MessageInput
                orgId={orgId}
                loading={isSending}
                autofocus={!!autofocus && isLanguageFocusable}
                subject={languageToSubject[language]}
                onSubjectChange={changeSubject}
                canPersonalize={Boolean(canPersonalize)}
                canUploadFile={canUploadFile}
                canTranslate={canTranslate}
                languagePreferenceBannerMessages={languagePreferenceBannerMessages}
                recipientType={recipientType}
              />
            </MessageComposerProvider>
            {(languageToMessage[language]?.includes('{{') || alwaysDisplayPreview) &&
              messagePreviewEmployees &&
              messagePreviewEmployees.length > 0 &&
              messagePreviewType && (
                <>
                  <div className="typo-title u-margin-top">Preview</div>

                  <LegacyMessageComposerPreview
                    // TODO[PROD-3453]: use context
                    languageCode={language}
                    messageType={messageType}
                    messageBody={
                      customizePreviewMessage
                        ? customizePreviewMessage(languageToMessage[language], language)
                        : languageToMessage[language]
                    }
                    subject={languageToSubject[language]}
                    messagePreviewEmployees={messagePreviewEmployees}
                    messagePreviewType={messagePreviewType}
                    urlFileMappings={languageToState[language].urlFileMappings}
                  />
                </>
              )}
          </div>
        );
      })}
      <div>
        <div
          id="button-container"
          className="u-margin-top-xs u-grid-auto-flow-column u-display-inlineGrid u-gap-10px"
        >
          {onSendMessage ? (
            showVCardButton ? (
              <UiSplitButton
                buttonGroupClassName="u-boxShadow-none"
                disabled={isSendingDisabled || isSending}
                MainButton={renderSendButton}
                DropdownButtons={renderContactCardButton}
                popperButtonClassName={buttonClass}
                popperButtonSize="sm"
              />
            ) : (
              <SendButton
                disabled={isSendingDisabled}
                showVCardButton={showVCardButton}
                isSending={isSending}
                sendMessage={sendMessage}
                displayedButtonText={displayedButtonText}
                buttonClass={buttonClass}
              />
            )
          ) : null}
          {canScheduleMessageInFuture ? (
            <UiButton
              variant="tertiary"
              onClick={handleToggleDialog}
              disabled={isScheduledMessageDisabled || isSendingDisabled}
              dataCy="schedule-message"
            >
              Schedule Message
            </UiButton>
          ) : null}
          {enableEmail && enableText && shouldShowMessageSwitcher && (
            <UiButton
              variant="tertiary"
              disabled={isSending || isLoading}
              onClick={handleSwitchType}
              dataCy="message-type-switcher"
            >
              Switch to {messageType === MESSAGE_TYPE.TEXT ? MESSAGE_TYPE.EMAIL : MESSAGE_TYPE.TEXT}
            </UiButton>
          )}
        </div>

        {
          /**
           * TODO: once we support composing messages in arbitrary languages
           * we'll want to make this into a split button that allows the
           * user to add arbitrary languages. For now we hardcode to Spanish
           */
          !disableAddLanguage &&
          languagesToSupport?.includes('es') &&
          !displayedLanguages.includes('es') ? (
            <UiButton
              onClick={handleAddLanguageClick('es')}
              variant="tertiary"
              dataCy="add-language-textbox-es"
            >
              Add Spanish version
            </UiButton>
          ) : null
        }
      </div>
      {isSchedulingMessage ? (
        <UiDialog title="Schedule Message" onClose={handleToggleDialog}>
          <UiDialog.Content>
            <div className="typo-title">Please select the time to send this message.</div>
            <DateTimePicker
              className="u-width-100 u-margin-bottom-md"
              label="Send Message At"
              value={sendMessageAt}
              onChange={handleScheduledTimeChange}
              disablePast
              strictCompareDates
              minDateMessage="Please select a time in the future"
            />
            {schedulingWarningMessage ? (
              <Message variant="warning" canBeDismissed={false}>
                {schedulingWarningMessage}
              </Message>
            ) : null}
          </UiDialog.Content>
          <UiDialog.Actions>
            <UiButton variant="dismiss" onClick={handleToggleDialog}>
              Cancel
            </UiButton>
            <UiButton
              className="u-margin-left"
              variant="primary"
              disabled={sendMessageAt === null || moment(sendMessageAt).isBefore(moment())}
              onClick={sendMessage()}
            >
              {displayedScheduleButtonText}
            </UiButton>
          </UiDialog.Actions>
        </UiDialog>
      ) : null}
    </>
  );
};
